import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  IconArrowHead,
  IconEdit,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';
import { useCartItems } from '../../../../util/cart';

import css from './TopbarDesktop.module.css';

const SignupLink = () => {
  return (
    <NamedLink id="signup-link" name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink id="login-link" name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const BOOK_LISTING_TYPE = 'book-listing';
const COLLECTOR_MERCH_LISTING_TYPE = 'collector-merch';
const ISO_LISTING_TYPE = 'iso-book';

const ShoppingBagIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5.5 8.5h13l-1 11h-11l-1-11Z" />
    <path d="M9 9V6.5a3 3 0 0 1 6 0V9" />
  </svg>
);

const CreateListingMenu = () => (
  <Menu id="create-listing-menu" contentPosition="left" skipFocusOnNavigation={false}>
    <MenuLabel
      id="create-listing-menu-label"
      className={css.createListingLabel}
      isOpenClassName={css.createListingOpen}
    >
      <IconEdit className={css.sellLinkIcon} />
      <span className={css.sellLinkLabel}>Create Listing</span>
      <IconArrowHead direction="down" size="tiny" rootClassName={css.createListingChevron} />
    </MenuLabel>
    <MenuContent className={css.createListingContent}>
      <MenuItem key="book-listing">
        <NamedLink
          className={css.createListingLink}
          name="NewListingPage"
          to={{ pathname: '/l/new', search: `?listingType=${BOOK_LISTING_TYPE}` }}
        >
          Sell a Book
        </NamedLink>
      </MenuItem>
      <MenuItem key="collector-merch">
        <NamedLink
          className={css.createListingLink}
          name="NewListingPage"
          to={{ pathname: '/l/new', search: `?listingType=${COLLECTOR_MERCH_LISTING_TYPE}` }}
        >
          Sell Bookish Goods
        </NamedLink>
      </MenuItem>
      <MenuItem key="iso-book">
        <NamedLink
          className={css.createListingLink}
          name="NewListingPage"
          to={{ pathname: '/l/new', search: `?listingType=${ISO_LISTING_TYPE}` }}
        >
          Post an ISO
        </NamedLink>
      </MenuItem>
    </MenuContent>
  </Menu>
);

const ProfileMenu = ({
  currentPage,
  currentUser,
  onLogout,
  showMyShopLink,
  inboxTab,
  notificationCount = 0,
  intl,
}) => {
  const displayName =
    currentUser?.attributes?.profile?.firstName ||
    currentUser?.attributes?.profile?.displayName ||
    '';
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <Menu skipFocusOnNavigation={true}>
      <MenuLabel
        id="profile-menu-label"
        className={css.profileMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
        ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.profileMenu' })}
      >
        <span className={css.profileMenuButtonInner}>
          <Avatar className={css.avatar} user={currentUser} disableProfileLink />
          {notificationCount > 0 ? (
            <span
              className={css.avatarNotificationDot}
              aria-label={`${notificationCount} unread conversations`}
            />
          ) : null}
        </span>
        <span className={css.profileGreeting}>
          <FormattedMessage id="TopbarDesktop.greeting" values={{ displayName }} />
        </span>
        <IconArrowHead direction="down" size="tiny" rootClassName={css.profileChevron} />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="InboxPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('InboxPage'))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <span className={css.menuItemBorder} />
            <span className={css.inboxLabel}>
              <FormattedMessage id="TopbarDesktop.inbox" />
              {notificationCount > 0 ? (
                <span
                  className={css.inboxNotificationDot}
                  role="img"
                  aria-label={intl.formatMessage(
                    { id: 'TopbarDesktop.inboxUnread' },
                    { count: notificationCount }
                  )}
                  data-testid="inbox-notification-dot"
                />
              ) : null}
            </span>
          </NamedLink>
        </MenuItem>
        {showMyShopLink && currentUser?.id?.uuid ? (
          <MenuItem key="ShopPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ShopPage'))}
              name="ShopPage"
              params={{ id: currentUser.id.uuid }}
            >
              <span className={css.menuItemBorder} />
              My Shop
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    showMyShopLink,
    inboxTab,
  } = props;
  const [mounted, setMounted] = useState(false);
  const cartItems = useCartItems();

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const listingActionsMaybe = showCreateListingsLink ? <CreateListingMenu /> : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showMyShopLink={showMyShopLink}
      inboxTab={inboxTab}
      notificationCount={notificationCount}
      intl={intl}
    />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );

  return (
    <nav
      className={classes}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.topbarNavigation' })}
    >
      <div className={css.utilityRow}>
        <LinkedLogo
          id="logo-topbar-desktop"
          className={css.logoLink}
          logoClassName={css.logoContainer}
          logoImageClassName={css.logoImage}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
          linkToExternalSite={config?.topbar?.logoLink}
        />

        {searchFormMaybe}

        <div className={css.actionsGroup}>
          {authenticatedOnClientSide ? (
            <NamedLink name="CartPage" className={css.cartLink} ariaLabel="Cart" title="Cart">
              <ShoppingBagIcon className={css.cartIcon} />
              {cartItems.length ? <span className={css.cartCount}>{cartItems.length}</span> : null}
            </NamedLink>
          ) : null}
          {profileMenuMaybe}
          {signupLinkMaybe}
          {loginLinkMaybe}
        </div>
      </div>

      <div className={css.navigationRow}>
        <div className={css.navigationSpacer} aria-hidden="true" />
        <CustomLinksMenu
          currentPage={currentPage}
          customLinks={customLinks}
          intl={intl}
          hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
          showCreateListingsLink={showCreateListingsLink}
        />
        <div className={css.navActions}>{listingActionsMaybe}</div>
      </div>
    </nav>
  );
};

export default TopbarDesktop;
